/**
 * Tab row — only rendered when a session has more than one tab.
 *
 * One browser session per thread is enforced server-side, so tabs are how a user
 * juggles several pages inside that one browser rather than a session switcher.
 * Duplicate labels get a secondary label appended, because two tabs both reading
 * "App" is worse than no label at all.
 *
 * @module BrowserTabsRow
 */
import type { BrowserCommand, BrowserTabSnapshot, DevTunnelRecord } from "@cmmd-forge/contracts";
import { GlobeIcon, XIcon } from "lucide-react";

import { cn } from "~/lib/utils";

import {
  browserTabDisplayLabel,
  browserTabDisplaySecondaryLabel,
} from "../../components/BrowserPreviewPanel.logic";
import type { BrowserChromeProps } from "./BrowserChrome";

export function BrowserPreviewTabsRow(props: BrowserChromeProps) {
  if (!props.session) {
    return null;
  }
  return (
    <BrowserPreviewTabs
      activeTabId={props.activeTab?.tabId}
      browserCommandBusy={props.browserCommandBusy}
      devTunnelRecords={props.devTunnelRecords}
      duplicateVisibleTabLabels={props.duplicateVisibleTabLabels}
      onRunCommand={(command) => void props.onRunCommand(command)}
      tabs={props.visibleTabs}
      visibleTabLabels={props.visibleTabLabels}
    />
  );
}

interface BrowserPreviewTabsProps {
  readonly activeTabId: string | null | undefined;
  readonly browserCommandBusy: boolean;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly duplicateVisibleTabLabels: ReadonlySet<string>;
  readonly onRunCommand: (command: BrowserCommand) => void;
  readonly tabs: ReadonlyArray<BrowserTabSnapshot>;
  readonly visibleTabLabels: ReadonlyArray<string>;
}

function BrowserPreviewTabs(props: BrowserPreviewTabsProps) {
  if (props.tabs.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 overflow-x-auto px-3 pb-2" aria-label="Browser tabs">
      {props.tabs.map((tab, index) => {
        const label = props.visibleTabLabels[index] ?? browserTabDisplayLabel(tab, index);
        const duplicateLabel = props.duplicateVisibleTabLabels.has(label);
        const secondaryLabel = duplicateLabel
          ? browserTabDisplaySecondaryLabel(tab, index, props.devTunnelRecords)
          : null;
        const tabDescription = secondaryLabel ? `${label}, ${secondaryLabel}` : label;
        const activateLabel = `Activate tab ${index + 1}: ${tabDescription}`;
        const closeLabel = `Close tab ${index + 1}: ${tabDescription}`;
        const active = tab.tabId === props.activeTabId;
        return (
          <div
            key={tab.tabId}
            className={cn(
              "inline-flex max-w-[220px] items-center rounded-md border text-xs",
              active
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            <button
              type="button"
              className="inline-flex min-w-0 items-center gap-1 px-2 py-1 text-left"
              aria-label={activateLabel}
              data-browser-tab-id={tab.tabId}
              onClick={() => props.onRunCommand({ type: "activateTab", tabId: tab.tabId })}
            >
              <GlobeIcon className="size-3 shrink-0" />
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate">{label}</span>
                {secondaryLabel ? (
                  <span className="truncate text-[10px] text-muted-foreground">
                    {secondaryLabel}
                  </span>
                ) : null}
              </span>
            </button>
            <button
              type="button"
              className="mr-1 inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={closeLabel}
              data-browser-close-tab-id={tab.tabId}
              disabled={props.browserCommandBusy}
              onClick={() => props.onRunCommand({ type: "closeTab", tabId: tab.tabId })}
            >
              <XIcon className="size-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
